import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    Box, Typography, TextField, Button, Grid, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, IconButton, Autocomplete, Card, CardContent, Divider, MenuItem, Radio
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CancelIcon from '@mui/icons-material/Cancel';
import PrintIcon from '@mui/icons-material/Print';
import Swal from 'sweetalert2';
import { db } from '../../firebase';
import { collection, addDoc, onSnapshot, query, where, updateDoc, doc, Timestamp, limit, getDocs, orderBy, deleteDoc } from 'firebase/firestore';
import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { storage } from "../../firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { Document, Page, Text, View, StyleSheet, pdf } from "@react-pdf/renderer";
import { DeleteOutline } from "@mui/icons-material";

const WeighbridgeSoftware = () => {
    const Toast = Swal.mixin({ toast: true, position: "top-end", showConfirmButton: false, timer: 4000, timerProgressBar: true });
    
    // Weight States
    const [grossWeight, setGrossWeight] = useState(0);
    const [tareWeight, setTareWeight] = useState(0);
    const [netWeight, setNetWeight] = useState(0);
    
    // Date Select States
    const [grossDate, setGrossDate] = useState(() => new Date().toISOString().slice(0, 16));
    const [tareDate, setTareDate] = useState(() => new Date().toISOString().slice(0, 16));

    const [selectedTripId, setSelectedTripId] = useState(null);
    const [dailyCreditLimit, setdailyCreditLimit] = useState(0);
    const [monthcreditLimit, setmonthCreditLimit] = useState(0);
    const [remainingCredit, setRemainingCredit] = useState(0);
    const [usedCredit, setUsedCredit] = useState(0);

    const [resetKey, setResetKey] = useState(0);
    const [printData, setPrintData] = useState(null);
    const [saleType, setSaleType] = useState("Volume Based");
    const [throughDetails, setThroughDetails] = useState([]);

    // Payment state for upfront payment handling
    const [paidAmount, setPaidAmount] = useState(0);

    const [productRows, setProductRows] = useState([{ productId: '', productName: '', qty: "", rate: "", kgrate: "", amount: 0 }]);
    const [openProductDialog, setOpenProductDialog] = useState(false);
    const [tempProduct, setTempProduct] = useState({ productId: "", productName: "", qty: "", rate: "", amount: 0 });

    const [formData, setFormData] = useState({
        customerId: '',
        customerName: '',
        vehicleNo: '',
        challan: Number(0),
        mobile: '',
        location: '',
        wbName: 'Tejas Stone Crusher',
        ticketNo: '',
        paymentMode: 'Cash',
        type: "Sale",
        invoice: "No",
        throughId: "",
        throughName: ""
    });

    const [customers, setCustomers] = useState([]);
    const [products, setProducts] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [paymentModes, setPaymentModes] = useState([]);

    useEffect(() => {
        const q = query(collection(db, "customers"), orderBy("customerName", "asc"));
        onSnapshot(q, (s) => {
            const allData = s.docs.map(d => ({
                id: d.id,
                customerName: d.data().customerName?.trim().toUpperCase() || "",
                mobile: d.data().mobile || "",
                location: d.data().city || "",
                dailyCreditLimit: d.data().dailyCreditLimit,
                monthlyCreditLimit: d.data().monthlyCreditLimit,
                products: d.data().products || []
            }));
            const uniqueCustomers = Array.from(new Map(allData.map(item => [item.customerName, item])).values());
            setCustomers(uniqueCustomers);
        });

        onSnapshot(collection(db, "trip_products"), (s) => {
            setProducts(s.docs.map(d => ({
                id: d.id,
                name: d.data().productName,
                tripType: d.data().tripType,
                rate: d.data().rate || 0,
                kgrate: d.data().kgrate,
            })));
        });

        onSnapshot(collection(db, "vehicles"), (s) => {
            setVehicles(s.docs.map(d => d.data().vehicalNo || ""));
        });

        onSnapshot(collection(db, "payment_modes"), (s) => {
            setPaymentModes(s.docs.map(d => d.data().payMode || ""));
        });

        onSnapshot(collection(db, "throughdetails"), (snapshot) => {
            setThroughDetails(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
    }, []);

    useEffect(() => {
        const net = Math.max(0, (Number(grossWeight) || 0) - (Number(tareWeight) || 0));
        setNetWeight(net);
    }, [grossWeight, tareWeight]);

    const handleProductChange = (index, value) => {
        const prod = products.find(p => p.name === value);
        let rate = prod?.rate || 0;
        const rows = [...productRows];
        rows[index].productId = prod?.id || '';
        rows[index].productName = value;
        if (saleType === "Weight Based") {
            rows[index].rate = Number(prod?.kgrate);
            rows[index].amount = Math.round(Number(netWeight) * Number(prod?.kgrate || 0));
        } else {
            rows[index].rate = rate;
            rows[index].amount = Math.round(Number(rows[index].qty || 0) * Number(rate || 0));
        }
        setProductRows(rows);
    };

    const handleRowChange = (index, field, value) => {
        const rows = [...productRows];
        rows[index][field] = Number(value);
        if (saleType === "Weight Based") {
            rows[index].amount = Math.round(Number(netWeight || 0) * Number(rows[index].rate || 0));
        } else {
            rows[index].amount = Math.round(Number(rows[index].qty || 0) * Number(rows[index].rate || 0));
        }
        setProductRows(rows);
    };

    const totalSale = productRows.reduce((sum, row) => sum + row.amount, 0);
    const calculatedRemaining = Math.max(0, totalSale - (Number(paidAmount) || 0));

    const handleProcess = async () => {
        try {
            if (!formData.vehicleNo) return Swal.fire("Error", "Vehicle number required", "error");
            if (!formData.customerName) return Swal.fire("Error", "Customer name required", "error");
            if (!formData.mobile) return Swal.fire("Error", "Mobile No is required", "error");

            const nextTicket = await generateTicketNo();
            const now = Timestamp.now();
            const customer = await getOrCreateCustomer(formData);

            const finalPaid = Number(paidAmount) || 0;
            const finalRemaining = Math.max(0, totalSale - finalPaid);

            const finalData = {
                ...formData,
                ticketNo: nextTicket,
                customerId: customer.id,
                customerName: customer.data.customerName,
                vehicleId: formData.vehicleNo,
                products: productRows,
                totalAmount: totalSale,
                paidAmount: finalPaid,
                remainingAmount: finalRemaining,
                saleType,
                grossWeight: Number(grossWeight),
                tareWeight: Number(tareWeight),
                netWeight: Number(netWeight),
                grossDate: grossDate ? Timestamp.fromDate(new Date(grossDate)) : now,
                tareDate: tareDate ? Timestamp.fromDate(new Date(tareDate)) : now,
                status: "Completed",
                createdAt: now,
                payments: finalPaid > 0 ? [{
                    amount: finalPaid,
                    mode: formData.paymentMode || "Cash",
                    date: now,
                    createdAt: now
                }] : []
            };

            const docRef = await addDoc(collection(db, "trips"), finalData);

            // Record payment history entry if any amount was paid
            if (finalPaid > 0) {
                await addDoc(collection(db, "customer_history"), {
                    customerId: customer.id,
                    amount: finalPaid,
                    paymentMode: formData.paymentMode || "Cash",
                    note: "Trip Upfront Payment",
                    date: now,
                    tripId: docRef.id,
                    type: "Trip",
                    createdAt: now
                });
            }

            setSelectedTripId(docRef.id);
            Swal.fire("Success", "Trip Saved Successfully!", "success");
            clearForm();
        } catch (e) {
            Swal.fire("Error", "Transaction failed: " + e.message, "error");
        }
    };

    const getOrCreateCustomer = async (formData) => {
        const customersRef = collection(db, "customers");
        const q = query(customersRef, where("customerName", "==", formData.customerName));
        const snapshot = await getDocs(q);

        if (!snapshot.empty) {
            return { id: snapshot.docs[0].id, data: snapshot.docs[0].data() };
        }

        const newCustomer = {
            customerName: formData.customerName || "",
            mobile: formData.mobile || "",
            address: formData.location || "",
            city: formData.location || "",
            createdAt: Timestamp.now(),
        };
        const docRef = await addDoc(customersRef, newCustomer);
        return { id: docRef.id, data: newCustomer };
    };

    const handlePartySelect = async (cust) => {
        if (!cust) return;
        setFormData(prev => ({
            ...prev,
            customerId: cust.id,
            customerName: cust.customerName,
            mobile: cust.mobile,
            location: cust.location
        }));
        setdailyCreditLimit(cust.dailyCreditLimit || 0);
        setmonthCreditLimit(cust.monthlyCreditLimit || 0);
    };

    const clearForm = () => {
        setFormData({
            customerId: "",
            customerName: "",
            vehicleNo: "",
            challan: Number(0),
            mobile: "",
            location: "",
            wbName: "Tejas Stone Crusher",
            ticketNo: "",
            paymentMode: "Cash",
            throughId: "",
            throughName: ""
        });
        setResetKey(prev => prev + 1);
        setProductRows([{ productId: "", productName: "", qty: 0, rate: 0, amount: 0 }]);
        setGrossWeight(0);
        setTareWeight(0);
        setNetWeight(0);
        setPaidAmount(0);
        setGrossDate(new Date().toISOString().slice(0, 16));
        setTareDate(new Date().toISOString().slice(0, 16));
        setSelectedTripId(null);
    };

    const generateTicketNo = async () => {
        const q = query(collection(db, "trips"), orderBy("ticketNo", "desc"), limit(1));
        const snap = await getDocs(q);
        if (snap.empty) return 1;
        return (snap.docs[0].data().ticketNo || 0) + 1;
    };

    const customerOptions = useMemo(() => {
        return customers.map(c => ({ label: c.customerName, ...c }));
    }, [customers]);

    return (
        <Box sx={{ bgcolor: '#f4f6f8', minHeight: '100vh', p: 2 }}>
            <Grid className="row" spacing={2}>
                <Grid item xs={12} className="col-sm-4">
                    <Paper elevation={3} sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Typography variant="h6" fontWeight="bold">Weight & Date Entry</Typography>
                        
                        <Box display="flex" flexDirection="column" gap={2}>
                            <TextField 
                                size="small" 
                                type="number" 
                                label="Gross Weight (kg)" 
                                value={grossWeight} 
                                onChange={(e) => setGrossWeight(e.target.value)} 
                            />
                            <TextField 
                                size="small" 
                                type="datetime-local" 
                                label="Gross Date & Time" 
                                InputLabelProps={{ shrink: true }}
                                value={grossDate} 
                                onChange={(e) => setGrossDate(e.target.value)} 
                            />

                            <TextField 
                                size="small" 
                                type="number" 
                                label="Tare Weight (kg)" 
                                value={tareWeight} 
                                onChange={(e) => setTareWeight(e.target.value)} 
                            />
                            <TextField 
                                size="small" 
                                type="datetime-local" 
                                label="Tare Date & Time" 
                                InputLabelProps={{ shrink: true }}
                                value={tareDate} 
                                onChange={(e) => setTareDate(e.target.value)} 
                            />

                            <TextField 
                                size="small" 
                                label="Net Weight (kg)" 
                                value={netWeight} 
                                disabled 
                                sx={{ bgcolor: '#e8f5e9' }}
                            />
                        </Box>
                    </Paper>
                </Grid>

                {/* RIGHT PANEL - MAIN ENTRY DETAILS */}
                <Grid item xs={12} className="col-sm-8">
                    <Paper elevation={3} sx={{ p: 2 }}>
                        <Typography variant="h6" fontWeight="bold" gutterBottom>Trip Transaction Details</Typography>
                        
                        <Grid className="row" spacing={2}>
                            <Grid className="col-sm-4 mb-3" item xs={6}>
                                <TextField fullWidth size="small" label="Challan No" value={formData.challan} onChange={(e) => setFormData({ ...formData, challan: e.target.value })} />
                            </Grid>

                            <Grid className="col-sm-4 mb-3" item xs={6}>
                                <Autocomplete
                                    freeSolo
                                    size="small"
                                    key={resetKey}
                                    options={customerOptions}
                                    getOptionLabel={(option) => typeof option === "string" ? option : option.customerName || ""}
                                    onChange={(e, val) => {
                                        if (val && typeof val !== "string") handlePartySelect(val);
                                        else if (typeof val === "string") setFormData(prev => ({ ...prev, customerName: val.toUpperCase() }));
                                    }}
                                    renderInput={(params) => <TextField {...params} label="Customer Name" fullWidth />}
                                />
                            </Grid>

                            <Grid className="col-sm-4 mb-3" item xs={6}>
                                <Autocomplete
                                    freeSolo
                                    size="small"
                                    options={vehicles}
                                    value={formData.vehicleNo || ""}
                                    onChange={(e, val) => setFormData({ ...formData, vehicleNo: val || "" })}
                                    onInputChange={(e, val) => setFormData({ ...formData, vehicleNo: val })}
                                    renderInput={(params) => <TextField {...params} label="Vehicle No" fullWidth />}
                                />
                            </Grid>

                            <Grid className="col-sm-4 mb-3" item xs={6}>
                                <TextField select fullWidth size="small" label="Sale Type" value={saleType} onChange={(e) => setSaleType(e.target.value)}>
                                    <MenuItem value="Weight Based">Weight Based</MenuItem>
                                    <MenuItem value="Volume Based">Volume Based</MenuItem>
                                </TextField>
                            </Grid>

                            <Grid className="col-sm-4 mb-3" item xs={6}>
                                <Autocomplete
                                    size="small"
                                    options={products.map(p => p.name)}
                                    value={productRows[0]?.productName || ""}
                                    onChange={(e, val) => handleProductChange(0, val)}
                                    renderInput={(params) => <TextField {...params} label="Product" fullWidth />}
                                />
                            </Grid>

                            {saleType !== "Weight Based" && (
                                <Grid className="col-sm-4 mb-3" item xs={6}>
                                    <TextField size="small" type="number" label="Brass / Qty" fullWidth value={productRows[0]?.qty} onChange={(e) => handleRowChange(0, "qty", e.target.value)} />
                                </Grid>
                            )}

                            <Grid className="col-sm-4 mb-3" item xs={6}>
                                <TextField size="small" type="number" label="Rate" fullWidth value={productRows[0]?.rate} onChange={(e) => handleRowChange(0, "rate", e.target.value)} />
                            </Grid>

                            <Grid className="col-sm-4 mb-3" item xs={6}>
                                <TextField size="small" label="Amount" fullWidth disabled value={productRows[0]?.amount} />
                            </Grid>

                            <Grid className="col-sm-4 mb-3" item xs={6}>
                                <TextField size="small" label="Mobile" fullWidth value={formData.mobile} onChange={(e) => setFormData({ ...formData, mobile: e.target.value })} />
                            </Grid>

                            <Grid className="col-sm-4 mb-3" item xs={6}>
                                <TextField size="small" label="Location" fullWidth value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} />
                            </Grid>

                            {/* Payment Section */}
                            <Grid className="col-sm-4 mb-3" item xs={6}>
                                <TextField 
                                    size="small" 
                                    type="number" 
                                    label="Paid Amount (₹)" 
                                    fullWidth 
                                    value={paidAmount} 
                                    onChange={(e) => setPaidAmount(e.target.value)} 
                                />
                            </Grid>

                            <Grid className="col-sm-4 mb-3" item xs={6}>
                                <TextField 
                                    size="small" 
                                    select 
                                    label="Payment Mode" 
                                    fullWidth 
                                    value={formData.paymentMode} 
                                    onChange={(e) => setFormData({ ...formData, paymentMode: e.target.value })}
                                >
                                    {paymentModes.map((mode, i) => (
                                        <MenuItem key={i} value={mode}>{mode}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        </Grid>

                        {/* Summary Display */}
                        <Box display="flex" justifyContent="space-between" alignItems="center" mt={3} p={2} bgcolor="#e8f5e9" borderRadius={1}>
                            <Box>
                                <Typography variant="subtitle1" fontWeight="bold" color="success.dark">Total Sale: ₹ {totalSale}</Typography>
                                <Typography variant="subtitle2" color="error.main">Remaining Balance: ₹ {calculatedRemaining}</Typography>
                            </Box>
                            <Box display="flex" gap={1}>
                                <Button variant="contained" color="success" onClick={handleProcess}>Save Trip</Button>
                                <Button variant="contained" color="error" onClick={clearForm}><CancelIcon /></Button>
                            </Box>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default WeighbridgeSoftware;